import Show from '@/components/Show';
import { useAppStore } from '@/state/appStore';
import useEditorStore from '@/state/editor-store';
import useGeneratorStore from '@/state/generator-store';
import { displayDuration } from '@/utils';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import PromptTextArea from '../PromptTextArea';
import ControlPanel from './ControlPanel';
import EditorHeader from './EditorHeader';
import './index.scss';
import Loading from './Loading';
import WaveformButtonGroup from './WaveformButtonGroup';
import WaveformDiv from './WaveformDiv';

const AudioEditor: React.FC = () => {
  const { loading, editor } = useEditorStore((state) => ({
    loading: state.loading,
    editor: state.editor,
  }));

  const { audioInfo } = useGeneratorStore((state) => ({
    audioInfo: state.audioInfo,
  }));

  const { pane } = useAppStore((state) => ({
    pane: state.pane,
  }));

  useEffect(() => {
    if (pane && editor) {
      const editorDataBtn = pane.addButton({
        title: 'editor',
        label: 'editor',
      });
      editorDataBtn.on('click', () => {
        console.info('editor:', editor);
      });

      const currentAudioInfoBtn = pane.addButton({
        title: 'audioInfo',
        label: 'audioInfo',
      });
      currentAudioInfoBtn.on('click', () => {
        console.info('audioInfo:', audioInfo);
      });

      return () => {
        pane.remove(editorDataBtn);
        pane.remove(currentAudioInfoBtn);
      };
    }
  }, [pane, editor]);

  return (
    <>
      <Show when={!loading} useVisibility={true} className="w-full">
        <div className="w-full">
          <EditorHeader />
          <div className="w-full flex flex-row editor-container">
            <div className="w-3/4">
              <div className="mt-6 justify-start items-end gap-6 inline-flex">
                <div className="text-[#dfdfdf] text-[31.26px] font-normal font-['NB International Pro'] leading-none">
                  {audioInfo?.name ?? 'Unnamed music'}
                </div>
                <div
                  className="text-[#dfdfdf] text-xs font-normal font-pressura-mono uppercase leading-none"
                  style={{
                    lineHeight: 'revert',
                  }}
                >
                  {displayDuration(audioInfo?.duration)}
                </div>
                <div
                  className="text-[#dfdfdf] text-xs font-normal font-pressura-mono uppercase leading-none"
                  style={{
                    lineHeight: 'revert',
                  }}
                >
                  {dayjs(audioInfo?.created_at).format('MMM D YYYY').toUpperCase()}
                </div>
              </div>
              <div className="w-[100%-100px] mt-4 mx-auto bg-zinc-900 text-zinc-100 rounded-lg shadow-lg p-1">
                <div className="w-full h-56 rounded-md flex items-center justify-center">
                  <WaveformDiv />
                </div>
                <div className="w-full">
                  <WaveformButtonGroup />
                </div>
              </div>
            </div>
            <div
              className="flex flex-col w-[100px] p-1 ml-2 bg-zinc-900 rounded-lg shadow-lg"
              style={{
                height: 'fit-content',
                marginTop: '24px',
              }}
            >
              <ControlPanel />
            </div>
            <div className="flex w-1/4">
              <div className="flex flex-col items-center">
                <div className="w-[24px] h-[360px] flex flex-col items-center gap-4">
                  <div className="w-[1px] h-28 bg-neutral-700" />
                  <div
                    className="[writing-mode:vertical-lr] rotate-180 whitespace-nowrap text-[9px] text-neutral-400 tracking-wider uppercase font-medium"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Editor Settings
                  </div>
                  <div className="w-[1px] h-28 bg-neutral-700" />
                </div>
              </div>
              <PromptTextArea />
            </div>
          </div>
        </div>
      </Show>
      <Show when={loading} useVisibility={true}>
        <Loading />
      </Show>
    </>
  );
};

export default AudioEditor;
